import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, Box, Button, Chip, Flex, Group, MultiSelect, Paper, Stack, Text, Textarea } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { CalendarMonthOutlined, EmailOutlined, LocalHospitalOutlined, PersonOutlined, PhoneOutlined, SchoolOutlined } from "@mui/icons-material";
import { SvgIcon } from "@mui/material";
import { useContext, useEffect } from "react";
import { DAYS } from "../../datasets/days_available";
import { ImagePrefixEnum, useCreateUserMutation, useGetLocationListQuery, useGetTeamMemberQuery, useGetTherapyCategoryListQuery, useGetTherapySubCategoryGroupsQuery, useUpdateUserMutation, UserRoleEnum } from "../../graphql/graphql";
import { useImageUpload } from "../../hooks/useImageUpload";
import { formatPhoneNumber } from "../../utils/PhoneNumber.utils";
import { CopyButton } from "../../shared/CopyComponents/CopyButton";
import { ImageUpload } from "../../shared/ImageUpload";
import { showErrorNotification, showSuccessNotification } from "../../shared/Notifications";
import { AppContext } from "../../shared/PrivateRoutes";
import { TeamDrawerInitialValues, TeamDrawerSchema } from "./TeamDrawer.validation";
// ─── Constants ──────────────────────────────────────────────────────
const ROLE_LABELS = {
    [UserRoleEnum.OrganizationOwner]: "Owner",
    [UserRoleEnum.OrganizationAdmin]: "Admin",
    [UserRoleEnum.CaseManager]: "Coordinator",
    [UserRoleEnum.Provider]: "Provider"
};
const ROLE_COLORS = {
    [UserRoleEnum.OrganizationOwner]: "grape",
    [UserRoleEnum.OrganizationAdmin]: "blue",
    [UserRoleEnum.CaseManager]: "teal",
    [UserRoleEnum.Provider]: "orange"
};
const PREFERRED_AGE_OPTIONS = [
    { value: "0-5", label: "0-5 yrs" },
    { value: "6-12", label: "6-12 yrs" },
    { value: "13-17", label: "13-17 yrs" },
    { value: "18+", label: "18+" }
];
// ─── Subcomponents ──────────────────────────────────────────────────
/** Section container with icon + title header */
const Section = ({ icon, title, children }) => (_jsx(Paper, { radius: "md", p: "md", withBorder: true, style: { borderColor: "var(--mantine-color-gray-2)" }, children: _jsxs(Stack, { gap: "sm", children: [_jsxs(Group, { gap: "xs", children: [_jsx(SvgIcon, { component: icon, style: {
                            fontSize: "1rem",
                            color: "var(--mantine-color-primary-5)"
                        } }), _jsx(Text, { size: "sm", fw: 600, c: "dimmed", children: title })] }), children] }) }));
/** Compact field label for within sections */
const FieldLabel = ({ children }) => (_jsx(Text, { size: "xs", fw: 500, c: "dimmed", children: children }));
// ─── Main component ─────────────────────────────────────────────────
export const TeamDrawerFormContent = ({ userId, active = true, onSuccess, onDirtyChange }) => {
    // Context
    const { isTherapproveAdmin, organizationId, userRole } = useContext(AppContext);
    const isOrgOwner = userRole === "OrganizationOwner";
    const canEdit = isTherapproveAdmin || isOrgOwner ||
        userRole === "OrganizationAdmin";
    const isCreate = !userId;
    // Image upload
    const { isUploadingImage, fetchingPresignedUrl, fetchingImageDetails, setCompressedImage, imageData } = useImageUpload();
    // Mutations
    const [{ fetching: createFetching }, createUser] = useCreateUserMutation();
    const [{ fetching: updateFetching }, updateUser] = useUpdateUserMutation();
    // Queries
    const [{ data: userData }] = useGetTeamMemberQuery({
        requestPolicy: "cache-and-network",
        variables: { userId: userId },
        pause: !userId || !active
    });
    const [{ data: locationData }] = useGetLocationListQuery({
        pause: !active
    });
    const locationOptions = locationData?.locations?.locationsList
        ?.filter(Boolean)
        .map((l) => ({ value: l.value, label: l.label })) ?? [];
    const [{ data: therapyCategoryData }] = useGetTherapyCategoryListQuery({
        variables: { inOrganization: true },
        pause: !active
    });
    const therapyCategoryOptions = therapyCategoryData?.therapyCategories?.therapyCategoriesList
        ?.filter(Boolean)
        .map((t) => ({ value: t.value, label: t.label })) ?? [];
    // Form
    const form = useForm({
        initialValues: TeamDrawerInitialValues,
        validate: yupResolver(TeamDrawerSchema)
    });
    const selectedRole = form.values.role;
    const isProvider = selectedRole === UserRoleEnum.Provider;
    const isDirty = form.isDirty();
    const fetching = createFetching || updateFetching;
    // Therapy sub-categories
    const [{ data: subCategoryData }] = useGetTherapySubCategoryGroupsQuery({
        variables: {
            therapyCategoryIds: form.values.therapyCategoryId
                ? [form.values.therapyCategoryId]
                : null
        },
        pause: !form.values.therapyCategoryId || !active
    });
    const therapySubCategoryOptions = subCategoryData?.therapySubCategories?.therapySubCategoryGroups?.flatMap((group) => group?.items?.filter(Boolean).map((i) => ({
        value: i.value,
        label: i.label
    })) ?? []) ?? [];
    // Effects
    useEffect(() => {
        if (imageData) {
            form.setFieldValue("imageKey", imageData.imageKey ?? null);
            form.setFieldValue("imageVersion", imageData.imageVersion ?? null);
        }
    }, [imageData]);
    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);
    const user = userData?.users?.user;
    useEffect(() => {
        if (!active)
            return;
        if (isCreate) {
            form.setValues(TeamDrawerInitialValues);
            setCompressedImage(null);
        }
        else if (user) {
            form.setValues({
                role: user.roleName ?? UserRoleEnum.Provider,
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                phoneNumber: user.phoneNumber,
                imageKey: user.imageKey ?? null,
                imageVersion: user.imageVersion ?? null,
                locationIds: user.locations
                    ?.map((l) => l.locationId)
                    .filter(Boolean) ?? [],
                daysAvailable: user.daysAvailable ?? [],
                about: user.about ?? "",
                notes: user.notes ?? "",
                educationAndCertifications: user.educationAndCertifications ?? "",
                preferredAges: user.preferredAges ?? [],
                therapyCategoryId: user.therapyCategory?.therapyCategoryId ?? null,
                therapySubCategoryIds: user.therapySubCategories
                    ?.map((s) => s.therapySubCategoryId)
                    .filter(Boolean) ?? []
            });
        }
        form.resetDirty();
    }, [active, userId, user]);
    // Handlers
    const handleSubmit = async (event) => {
        event.preventDefault();
        const values = form.values;
        if (!values.firstName || !values.lastName || !values.email || !values.phoneNumber) {
            showErrorNotification("Please fill in all required fields.");
            return;
        }
        try {
            if (isCreate) {
                const result = await createUser({
                    input: {
                        role: values.role,
                        organizationId,
                        firstName: values.firstName,
                        lastName: values.lastName,
                        email: values.email,
                        phoneNumber: values.phoneNumber,
                        imageKey: values.imageKey,
                        imageVersion: values.imageVersion,
                        locationIds: values.locationIds.length
                            ? values.locationIds
                            : undefined,
                        daysAvailable: values.daysAvailable.length
                            ? values.daysAvailable
                            : undefined,
                        ...(isProvider && {
                            about: values.about || undefined,
                            notes: values.notes || undefined,
                            educationAndCertifications: values.educationAndCertifications || undefined,
                            preferredAges: values.preferredAges.length
                                ? values.preferredAges
                                : undefined,
                            therapyCategoryId: values.therapyCategoryId || undefined,
                            therapySubCategoryIds: values.therapySubCategoryIds.length
                                ? values.therapySubCategoryIds
                                : undefined
                        })
                    }
                });
                if (!result.data?.users?.createUser) {
                    throw new Error("Failed to create team member.");
                }
                showSuccessNotification(`${values.firstName} ${values.lastName} was added to the team.`);
            }
            else {
                const result = await updateUser({
                    input: {
                        userId: userId,
                        firstName: values.firstName,
                        lastName: values.lastName,
                        email: values.email,
                        phoneNumber: values.phoneNumber,
                        imageKey: values.imageKey,
                        imageVersion: values.imageVersion,
                        locationIds: values.locationIds,
                        daysAvailable: values.daysAvailable,
                        ...(isProvider && {
                            about: values.about || undefined,
                            notes: values.notes || undefined,
                            educationAndCertifications: values.educationAndCertifications || undefined,
                            preferredAges: values.preferredAges,
                            therapyCategoryId: values.therapyCategoryId || undefined,
                            therapySubCategoryIds: values.therapySubCategoryIds
                        })
                    }
                });
                if (!result.data?.users?.updateUser) {
                    throw new Error("Failed to update team member.");
                }
                showSuccessNotification(`${values.firstName} ${values.lastName} was updated.`);
            }
            form.resetDirty();
            onSuccess?.();
        }
        catch (error) {
            console.error(error);
            showErrorNotification();
        }
    };
    // Roles the current user can assign
    const availableRoles = Object.entries(ROLE_LABELS).filter(([role]) => {
        if (role === UserRoleEnum.OrganizationOwner) {
            return isTherapproveAdmin || isOrgOwner;
        }
        return true;
    });
    const roleBadgeColor = ROLE_COLORS[selectedRole] ?? "gray";
    // Select all text on focus so typing replaces the existing value
    const selectAllOnFocus = (e) => {
        const selection = window.getSelection();
        if (!selection)
            return;
        const range = document.createRange();
        range.selectNodeContents(e.currentTarget);
        selection.removeAllRanges();
        selection.addRange(range);
    };
    // Strip leftover <br> / empty text nodes so :empty works for the placeholder
    const cleanEmpty = (e) => {
        const el = e.currentTarget;
        if (!el.textContent?.trim()) {
            el.innerHTML = "";
        }
    };
    return (_jsxs(Stack, { gap: 0, style: { flex: 1, minHeight: 0 }, children: [_jsx("div", { style: { flex: 1, overflowY: "auto", overflowX: "hidden" }, children: _jsxs(Stack, { gap: "md", pb: "md", children: [_jsx(Box, { py: "lg", px: "md", style: { backgroundColor: "var(--mantine-color-primary-0)" }, children: _jsxs(Group, { gap: "md", wrap: "nowrap", align: "center", children: [_jsx(ImageUpload, { setCompressedImage: setCompressedImage, imageUrl: user?.imageUrl ?? null, imageKey: form.values.imageKey, imageVersion: form.values.imageVersion, imagePrefix: ImagePrefixEnum.Avatars, size: "5rem" }), _jsxs(Stack, { gap: 4, style: { flex: 1, minWidth: 0 }, children: [_jsxs(Group, { gap: 6, wrap: "wrap", children: [_jsx(Text, { fw: 700, style: { fontSize: "1.5rem", whiteSpace: "nowrap" }, c: "gray.9", contentEditable: canEdit, suppressContentEditableWarning: true, "data-editable-field": true, "data-placeholder": "First", onFocus: selectAllOnFocus, onInput: cleanEmpty, onBlur: (e) => {
                                                            form.setFieldValue("firstName", (e.currentTarget.textContent ?? "").trim());
                                                        }, children: form.values.firstName || null }), _jsx(Text, { fw: 700, style: { fontSize: "1.5rem", whiteSpace: "nowrap" }, c: "gray.9", contentEditable: canEdit, suppressContentEditableWarning: true, "data-editable-field": true, "data-placeholder": "Last", onFocus: selectAllOnFocus, onInput: cleanEmpty, onBlur: (e) => {
                                                            form.setFieldValue("lastName", (e.currentTarget.textContent ?? "").trim());
                                                        }, children: form.values.lastName || null })] }), isCreate ? (_jsx(Chip.Group, { value: form.values.role, onChange: (value) => form.setFieldValue("role", value), children: _jsx(Flex, { gap: 6, wrap: "wrap", children: availableRoles.map(([value, label]) => (_jsx(Chip, { value: value, size: "xs", disabled: !canEdit, children: label }, value))) }) })) : (_jsx("div", { children: _jsx(Badge, { size: "sm", variant: "light", color: roleBadgeColor, children: ROLE_LABELS[selectedRole] ?? selectedRole }) }))] })] }) }), _jsxs(Stack, { gap: "sm", children: [_jsx(Section, { icon: PersonOutlined, title: "Contact", children: _jsxs(Stack, { gap: "xs", align: "flex-start", children: [_jsxs(Group, { gap: "xs", wrap: "nowrap", children: [_jsx(SvgIcon, { component: PhoneOutlined, style: {
                                                            fontSize: "0.9rem",
                                                            color: "var(--mantine-color-gray-5)",
                                                            flexShrink: 0
                                                        } }), _jsx(Text, { size: "sm", c: "gray.8", fw: 600, style: { whiteSpace: "nowrap" }, contentEditable: canEdit, suppressContentEditableWarning: true, "data-editable-field": true, "data-placeholder": "Phone number", onFocus: selectAllOnFocus, onInput: cleanEmpty, onBlur: (e) => {
                                                            form.setFieldValue("phoneNumber", formatPhoneNumber((e.currentTarget.textContent ?? "").trim()));
                                                        }, children: form.values.phoneNumber || null }), form.values.phoneNumber && (_jsx(CopyButton, { value: form.values.phoneNumber }))] }), _jsxs(Group, { gap: "xs", wrap: "nowrap", children: [_jsx(SvgIcon, { component: EmailOutlined, style: {
                                                            fontSize: "0.9rem",
                                                            color: "var(--mantine-color-gray-5)",
                                                            flexShrink: 0
                                                        } }), _jsx(Text, { size: "sm", c: "gray.8", fw: 600, style: { whiteSpace: "nowrap" }, contentEditable: canEdit, suppressContentEditableWarning: true, "data-editable-field": true, "data-placeholder": "Email address", onFocus: selectAllOnFocus, onInput: cleanEmpty, onBlur: (e) => {
                                                            form.setFieldValue("email", (e.currentTarget.textContent ?? "").trim().toLowerCase());
                                                        }, children: form.values.email || null }), form.values.email && (_jsx(CopyButton, { value: form.values.email }))] })] }) }), _jsx(Section, { icon: CalendarMonthOutlined, title: "Scheduling", children: _jsxs(Stack, { gap: "sm", children: [_jsxs(Stack, { gap: 4, children: [_jsx(FieldLabel, { children: "Locations" }), _jsx(MultiSelect, { variant: "pill", placeholder: "Select locations", data: locationOptions, disabled: !canEdit, searchable: true, clearable: true, size: "xs", styles: {
                                                            input: {
                                                                border: "none",
                                                                backgroundColor: "transparent",
                                                                padding: 0,
                                                                minHeight: "auto"
                                                            }
                                                        }, ...form.getInputProps("locationIds") })] }), _jsxs(Stack, { gap: 4, children: [_jsx(FieldLabel, { children: "Availability" }), _jsx(Chip.Group, { multiple: true, value: form.values.daysAvailable, onChange: (value) => form.setFieldValue("daysAvailable", value), children: _jsx(Flex, { gap: 6, wrap: "wrap", children: DAYS.map((day) => (_jsx(Chip, { value: day, size: "xs", disabled: !canEdit, children: day.slice(0, 3) }, day))) }) })] })] }) }), isProvider && (_jsx(Section, { icon: LocalHospitalOutlined, title: "Clinical Profile", children: _jsxs(Stack, { gap: "sm", children: [_jsxs(Stack, { gap: 4, children: [_jsx(FieldLabel, { children: "Therapy Modality" }), _jsx(MultiSelect, { variant: "pill", placeholder: "Select modality", data: therapyCategoryOptions, disabled: !canEdit, searchable: true, clearable: true, maxValues: 1, size: "xs", styles: {
                                                            input: {
                                                                border: "none",
                                                                backgroundColor: "transparent",
                                                                padding: 0,
                                                                minHeight: "auto"
                                                            }
                                                        }, value: form.values.therapyCategoryId
                                                            ? [form.values.therapyCategoryId]
                                                            : [], onChange: (value) => form.setFieldValue("therapyCategoryId", value[0] ?? null) })] }), form.values.therapyCategoryId && (_jsxs(Stack, { gap: 4, children: [_jsx(FieldLabel, { children: "Skills" }), _jsx(MultiSelect, { variant: "pill", placeholder: "Select skills", data: therapySubCategoryOptions, disabled: !canEdit, searchable: true, clearable: true, size: "xs", styles: {
                                                            input: {
                                                                border: "none",
                                                                backgroundColor: "transparent",
                                                                padding: 0,
                                                                minHeight: "auto"
                                                            }
                                                        }, ...form.getInputProps("therapySubCategoryIds") })] })), _jsxs(Stack, { gap: 4, children: [_jsx(FieldLabel, { children: "Preferred Ages" }), _jsx(Chip.Group, { multiple: true, value: form.values.preferredAges, onChange: (value) => form.setFieldValue("preferredAges", value), children: _jsx(Flex, { gap: 6, wrap: "wrap", children: PREFERRED_AGE_OPTIONS.map((opt) => (_jsx(Chip, { value: opt.value, size: "xs", disabled: !canEdit, children: opt.label }, opt.value))) }) })] })] }) })), isProvider && (_jsx(Section, { icon: SchoolOutlined, title: "About", children: _jsxs(Stack, { gap: "sm", children: [_jsxs(Stack, { gap: 4, children: [_jsx(FieldLabel, { children: "Bio" }), _jsx(Textarea, { variant: "unstyled", placeholder: "A short bio or description...", disabled: !canEdit, autosize: true, minRows: 2, size: "xs", styles: {
                                                            input: {
                                                                border: "none",
                                                                backgroundColor: "transparent",
                                                                padding: 0,
                                                                fontSize: "0.85rem",
                                                                color: "var(--mantine-color-gray-7)"
                                                            }
                                                        }, ...form.getInputProps("about") })] }), _jsxs(Stack, { gap: 4, children: [_jsx(FieldLabel, { children: "Education & Certifications" }), _jsx(Textarea, { variant: "unstyled", placeholder: "Degrees, certifications, licenses...", disabled: !canEdit, autosize: true, minRows: 2, size: "xs", styles: {
                                                            input: {
                                                                border: "none",
                                                                backgroundColor: "transparent",
                                                                padding: 0,
                                                                fontSize: "0.85rem",
                                                                color: "var(--mantine-color-gray-7)"
                                                            }
                                                        }, ...form.getInputProps("educationAndCertifications") })] }), _jsxs(Stack, { gap: 4, children: [_jsx(FieldLabel, { children: "Internal Notes" }), _jsx(Textarea, { variant: "unstyled", placeholder: "Notes visible only to your team...", disabled: !canEdit, autosize: true, minRows: 2, size: "xs", styles: {
                                                            input: {
                                                                border: "none",
                                                                backgroundColor: "transparent",
                                                                padding: 0,
                                                                fontSize: "0.85rem",
                                                                color: "var(--mantine-color-gray-7)"
                                                            }
                                                        }, ...form.getInputProps("notes") })] })] }) }))] })] }) }), canEdit && isDirty && (_jsxs(Flex, { gap: "sm", px: "md", py: "sm", style: {
                    borderTop: "1px solid var(--mantine-color-gray-2)",
                    backgroundColor: "var(--mantine-color-body)"
                }, children: [_jsx(Button, { variant: "filled", loading: fetching, disabled: isUploadingImage ||
                            fetchingPresignedUrl ||
                            fetchingImageDetails, onClick: handleSubmit, children: isCreate ? "Add to Team" : "Save Changes" }), _jsx(Button, { variant: "subtle", c: "dimmed", onClick: () => {
                            form.reset();
                            form.resetDirty();
                        }, children: "Cancel" })] }))] }));
};
